import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { Card, Form } from 'react-bootstrap';
import { formatCurrency } from '../../shared/utils/currency';
import { computeNetWorthByMonth } from './NetWorthTrendChart';

const GROWTH_COLOR = '#28a745';
const DECLINE_COLOR = '#e74c3c';

// Monthly: change from the prior month. Yearly: change from the prior
// year's last available month (year-end-ish net worth) to this year's.
const computeGrowth = (data, mode) => {
  const monthly = computeNetWorthByMonth(data);
  if (monthly.length < 2) return [];

  if (mode === 'Monthly') {
    return monthly.slice(1).map((entry, i) => {
      const prev = monthly[i].netWorth;
      const delta = entry.netWorth - prev;
      return {
        period: entry.YearMonth,
        delta,
        deltaPct: prev !== 0 ? (delta / Math.abs(prev)) * 100 : null,
      };
    });
  }

  // Yearly: last known net worth per year, then diff consecutive years.
  const byYear = new Map();
  monthly.forEach(({ YearMonth, netWorth }) => {
    const year = YearMonth.slice(0, 4);
    byYear.set(year, netWorth); // later months overwrite earlier ones within the same year
  });
  const years = Array.from(byYear.keys()).sort();
  return years.slice(1).map((year, i) => {
    const prev = byYear.get(years[i]);
    const curr = byYear.get(year);
    const delta = curr - prev;
    return {
      period: year,
      delta,
      deltaPct: prev !== 0 ? (delta / Math.abs(prev)) * 100 : null,
    };
  });
};

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    const { delta, deltaPct } = payload[0].payload;
    return (
      <div className="custom-tooltip card p-2">
        <p className="label mb-1">{label}</p>
        <p className="mb-0" style={{ color: delta >= 0 ? GROWTH_COLOR : DECLINE_COLOR, fontWeight: 'bold' }}>
          {`${delta >= 0 ? '+' : ''}${formatCurrency(delta, currency)}`}
          {deltaPct !== null ? ` (${delta >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%)` : ''}
        </p>
      </div>
    );
  }
  return null;
};

const AssetGrowthChart = ({ data, currency }) => {
  const [mode, setMode] = useState('Monthly');
  const chartData = computeGrowth(data, mode);

  return (
    <Card className="mb-4 shadow-lg">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Net Worth Growth</span>
        <Form.Select
          size="sm"
          style={{ width: '140px' }}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="Monthly">Month-over-Month</option>
          <option value="Yearly">Year-over-Year</option>
        </Form.Select>
      </Card.Header>
      <Card.Body style={{ height: '350px' }}>
        {chartData.length === 0 ? (
          <p>Not enough data yet to compute {mode === 'Monthly' ? 'month-over-month' : 'year-over-year'} growth.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis tickFormatter={(value) => formatCurrency(value, currency)} width={90} />
              <Tooltip content={<CustomTooltip currency={currency} />} />
              <ReferenceLine y={0} stroke="#888" />
              <Bar dataKey="delta" name="Change">
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.delta >= 0 ? GROWTH_COLOR : DECLINE_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card.Body>
    </Card>
  );
};

export default AssetGrowthChart;
